/**
 * "New version installed" and "reload to use the new version" notices (#3315).
 *
 * - New version: the server's SHA differs from the one this user last saw on
 *   this device. Shown once; closing or opening "What's new" acknowledges it.
 *   The first visit only records the version.
 * - Stale tab: the server runs a different build than this tab loaded. The
 *   reload notice takes precedence; the new-version notice follows after it.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Button, IconButton, Snackbar, Stack, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatDateTime } from '@/utils/locale';
import { useVersionAckKey, useVersionInfo } from './useVersionInfo';
import {
  acknowledgeVersion,
  buildShaFromAppVersion,
  decideVersionNotice,
  isStaleTab,
  readVersionAck,
  writeVersionAck,
} from './versionNotice';
import { useWhatsNewStore } from './whatsNewStore';

const FRONTEND_SHA = buildShaFromAppVersion(typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '');

export function VersionUpdateNotice({
  frontendSha = FRONTEND_SHA,
}: {
  /** The build this tab loaded; a prop only so tests can set it. */
  frontendSha?: string | null;
}) {
  const { t } = useTranslation('releases');
  const { t: tc } = useTranslation('common');
  const { data: info } = useVersionInfo();
  const ackKey = useVersionAckKey();
  const openWhatsNew = useWhatsNewStore((s) => s.openWhatsNew);
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [staleDismissedFor, setStaleDismissedFor] = useState<string | null>(null);

  const serverSha = info?.sha ?? null;
  const stale = isStaleTab(frontendSha, serverSha);

  useEffect(() => {
    if (ackKey === null || stale) {
      setShowNewVersion(false);
      return;
    }
    const decision = decideVersionNotice(readVersionAck(window.localStorage, ackKey), serverSha);
    if (decision.kind === 'record-first-seen' && serverSha !== null) {
      writeVersionAck(window.localStorage, ackKey, acknowledgeVersion(null, serverSha));
    }
    setShowNewVersion(decision.kind === 'show');
  }, [ackKey, serverSha, stale]);

  const acknowledge = () => {
    if (ackKey !== null && serverSha !== null) {
      const ack = readVersionAck(window.localStorage, ackKey);
      writeVersionAck(window.localStorage, ackKey, acknowledgeVersion(ack, serverSha));
    }
    setShowNewVersion(false);
  };

  if (stale && serverSha !== null && staleDismissedFor !== serverSha) {
    return (
      <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity="warning"
          variant="filled"
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  window.location.reload();
                }}
              >
                {t('notice.reload')}
              </Button>
              <Tooltip title={tc('close')}>
                <IconButton
                  color="inherit"
                  size="small"
                  aria-label={tc('close')}
                  onClick={() => {
                    setStaleDismissedFor(serverSha);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        >
          <AlertTitle>{t('notice.reloadTitle')}</AlertTitle>
          {t('notice.reloadBody')}
        </Alert>
      </Snackbar>
    );
  }

  if (!showNewVersion || info === undefined) return null;

  return (
    <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert
        severity="info"
        variant="filled"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                acknowledge();
                openWhatsNew();
              }}
            >
              {t('notice.showWhatsNew')}
            </Button>
            <Tooltip title={tc('close')}>
              <IconButton color="inherit" size="small" aria-label={tc('close')} onClick={acknowledge}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      >
        {t('notice.installed', { date: formatDateTime(info.installedAt) })}
      </Alert>
    </Snackbar>
  );
}
